import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';


import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Spinner } from '@edx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';

import {
  backupRegistrationFormBegin,
  clearRegistrationBackendError,
  resetSubmitState,
  setUserPipelineDataLoaded,
} from './data/actions';
import { REGISTRATION_CONFIRMATION_STEP_NUMBER, TPA_AUTHENTICATION_FAILURE } from './data/constants';
import messages from './messages';
import AdditionalUserInformation from './steps/AdditionalUserInformation';
import EmailVerification from './steps/EmailVerification';
import ManualAccountCreation from './steps/ManualAccountCreation';
import RegistrationConfirmation from './steps/RegistrationConfirmation';
import RegistrationProcessCompletion from './steps/RegistrationProcessCompletion';
import RegistrationMethod from './steps/RegistrationMethod';
import {
  InstitutionLogistration,
} from '../common-components';
import {
  getThirdPartyAuthContext as getRegistrationDataFromBackend,
  clearRedirectTarget,
  setAuthenticatedUser,
} from '../common-components/data/actions';
import EnterpriseSSO from '../common-components/EnterpriseSSO';
import {
  COMPLETE_STATE,
  PENDING_STATE,
  REGISTER_PAGE,
} from '../data/constants';
import {
  getAllPossibleQueryParams, getTpaHint, getTpaProvider, isHostAvailableInQueryParams, setCookie,
} from '../data/utils';
import { firePageView } from '../login/data/gtm';
/**
 * Main Registration Page component
 */
const RegistrationPage = (props) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const registrationEmbedded = isHostAvailableInQueryParams();

  const { handleInstitutionLogin, institutionLogin } = props;

  const {
    registrationFormData: backedUpFormData,
    registrationError,
    registrationError: {
      errorCode: registrationErrorCode,
    } = {},
    registrationResult,
    shouldBackupState,
    userPipelineDataLoaded,
  } = useSelector(state => state.register);

  const {
    thirdPartyAuthApiStatus,
    thirdPartyAuthContext,
    authenticatedUser,
    thirdPartyAuthContext: {
      autoSubmitRegForm,
      errorMessage: thirdPartyAuthErrorMessage,
      providers,
      secondaryProviders,
      pipelineUserDetails,
    },
    redirectTarget,
  } = useSelector(state => state.commonComponents);

  const queryParams = useMemo(() => getAllPossibleQueryParams(), []);
  const tpaHint = useMemo(() => getTpaHint(), []);

  const [formFields, setFormFields] = useState({ ...backedUpFormData.formFields });
  
  // helper function for adding additional fields to the form (split full name to first and last name)
  const updateForm = (updates) =>
    setFormFields((prev) => ({ ...prev, ...updates }));

  const [errorCode, setErrorCode] = useState({ type: '', count: 0 });
  const [step, setStep] = useState(0);
  const [formStartTime, setFormStartTime] = useState(null);

  const STEP = {
    REGISTRATION_METHOD: 0,
    MANUAL_ACCOUNT_CREATION: 1,
    EMAIL_VERIFICATION: 2,
    REGISTRATION_CONFIRMATION: 3,
    ADDITIONAL_USER_INFORMATION: 4,
    REGISTRATION_PROCESS_COMPLETION: 5,
  };

  const currentProvider = thirdPartyAuthContext?.currentProvider;

  function getStepClass(step, currentProvider) {
    const StepWithFullWidth = new Set([
      STEP.EMAIL_VERIFICATION,
      STEP.REGISTRATION_CONFIRMATION,
      STEP.ADDITIONAL_USER_INFORMATION,
      STEP.REGISTRATION_PROCESS_COMPLETION,
    ]);
    
    if (step === STEP.MANUAL_ACCOUNT_CREATION && currentProvider) {
      return 'logistration-full-width';
    }

    if (StepWithFullWidth.has(step)) {
      return 'logistration-full-width';
    }

    return '';
  }

  // temporary error state for embedded experience because we don't want to show errors on blur
  // const [temporaryErrors, setTemporaryErrors] = useState({ ...backedUpFormData.errors });

  /**
   * Set the userPipelineDetails data in formFields for only first time
   */
  useEffect(() => {
    if (!userPipelineDataLoaded && thirdPartyAuthApiStatus === COMPLETE_STATE) {
      if (thirdPartyAuthErrorMessage) {
        setErrorCode(prevState => ({ type: TPA_AUTHENTICATION_FAILURE, count: prevState.count + 1 }));
      }
      if (pipelineUserDetails && Object.keys(pipelineUserDetails).length !== 0) {
        const { name = '', username = '', email = '' } = pipelineUserDetails;
        const full_name = name;
        setFormFields(prevState => ({
          ...prevState, name, username, email, full_name
        }));
        dispatch(setUserPipelineDataLoaded(true));
      }
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    thirdPartyAuthContext,
    userPipelineDataLoaded,
  ]);

  useEffect(() => {
    if ((queryParams.account_activation_status) && authenticatedUser) {
      setStep(REGISTRATION_CONFIRMATION_STEP_NUMBER);
    }

    if (queryParams.step === 'confirmation' && authenticatedUser) {
      setStep(STEP.REGISTRATION_CONFIRMATION);
    }
  }, [queryParams, authenticatedUser, STEP.REGISTRATION_CONFIRMATION]);

  useEffect(() => {
    if (!formStartTime) {
      sendPageEvent('login_and_registration', 'register');
      const payload = { ...queryParams, is_register_page: true };
      if (tpaHint) {
        payload.tpa_hint = tpaHint;
      }
      dispatch(getRegistrationDataFromBackend(payload));
      setFormStartTime(Date.now());
    }
  }, [dispatch, formStartTime, queryParams, tpaHint]);

  /**
   * Backup the registration form in redux when register page is toggled.
   */
  useEffect(() => {
    if (shouldBackupState) {
      dispatch(backupRegistrationFormBegin({
        ...backedUpFormData,
        // configurableFormFields: { ...configurableFormFields },
        formFields: { ...formFields },
      }));
    }
  }, [shouldBackupState, formFields, dispatch, backedUpFormData]);

  useEffect(() => {
    if (registrationErrorCode) {
      setErrorCode(prevState => ({ type: registrationErrorCode, count: prevState.count + 1 }));
    }
  }, [registrationErrorCode]);

  useEffect(() => {
    if (registrationResult.success) {
      // This event is used by GTM
      sendTrackEvent('edx.bi.user.account.registered.client', {});

      // This is used by the "User Retention Rate Event" on GTM
      setCookie(getConfig().USER_RETENTION_COOKIE_NAME, true);

      // Sync authenticated user to commonComponents state
      if (registrationResult.authenticatedUser) {
        dispatch(setAuthenticatedUser(registrationResult.authenticatedUser));
      }

      if (registrationResult.redirectUrl && currentProvider) {

        if (registrationResult.redirectUrl.includes('/auth/complete/')) {
          const completeUrl = new URL(registrationResult.redirectUrl, getConfig().LMS_BASE_URL);

          fetch(completeUrl.toString(), {
            method: 'GET',
            credentials: 'include',
            redirect: 'manual',
          })
            .then(() => {
              setStep(STEP.REGISTRATION_CONFIRMATION);
            })
            .catch((error) => {
              console.error('Error completing pipeline:', error);
              setStep(STEP.REGISTRATION_CONFIRMATION);
            });
        }
      }
    }
  }, [registrationResult, dispatch, currentProvider]);

  const handleOnChange = (event) => {
    const { name } = event.target;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (registrationError[name]) {
      dispatch(clearRegistrationBackendError(name));
    }
    setFormFields(prevState => ({ ...prevState, [name]: value }));
  };

  const handleGoToNextStep = () => setStep((prev) => prev + 1);
  const handleGoToPrevStep = () => setStep((prev) => prev - 1);

  const handleBackFromEmailVerification = () => {
    dispatch(resetSubmitState());
    setStep((prev) => prev - 1);
  };
  
  useEffect(() => {
    if(!redirectTarget) return;

    if (redirectTarget === REGISTER_PAGE) {
      setStep(STEP.MANUAL_ACCOUNT_CREATION);
      dispatch(clearRedirectTarget());
    }

  }, [redirectTarget, dispatch]);

  const renderForm = () => {
    if (institutionLogin) {
      return (
        <InstitutionLogistration
          secondaryProviders={secondaryProviders}
          headingTitle={formatMessage(messages['register.institution.login.page.title'])}
        />
      );
    }

  const { locale } = useIntl(); 
  const location = useLocation();
  const sent = useRef(false);

  useEffect(() => {
    if (!sent.current) {
      firePageView('login_signup_page_view_lang', { locale, location });
      sent.current = true;
    }
  }, [locale, location]);


    return (
      <>
        <Helmet>
          <title>{formatMessage(messages['register.page.title'], { siteName: getConfig().SITE_NAME })}</title>
        </Helmet>
        {autoSubmitRegForm && !errorCode.type ? (
          <div className="mw-xs mt-5 text-center">
            <Spinner animation="border" variant="primary" id="tpa-spinner" />
          </div>
        ) : (
          <div className={classNames('form-w', { 'w-100 m-auto main-content': registrationEmbedded })}>
            <div className="d-flex flex-column w-100 h-100 form-login-register-wrapper">
              <div className={`flex-center-column form-login-register-background ${getStepClass(step, currentProvider)}`}> 
                {step === STEP.REGISTRATION_METHOD && (
                  <RegistrationMethod
                    thirdPartyAuthApiStatus={thirdPartyAuthApiStatus}
                    providers={providers}
                    secondaryProviders={secondaryProviders}
                    currentProvider={currentProvider}
                    handleGoToNextStep={handleGoToNextStep}
                    handleInstitutionLogin={handleInstitutionLogin}
                   />
                )}
                {step === STEP.MANUAL_ACCOUNT_CREATION && (
                  <ManualAccountCreation
                    providers={providers}
                    referrer={REGISTER_PAGE}
                    onSelectPage={props.onSelectPage}
                    formFields={formFields}
                    updateForm={updateForm}
                    setErrorCode={setErrorCode}
                    handleOnChange={handleOnChange}
                    handleGoToNextStep={handleGoToNextStep}
                    handleGoToPrevStep={handleGoToPrevStep}
                    queryParams={queryParams}
                    formStartTime={formStartTime}
                    errorCode={errorCode}
                  />
                )}
                {step === STEP.EMAIL_VERIFICATION && (
                  <EmailVerification
                    formFields={formFields}
                    handleGoToNextStep={handleGoToNextStep}
                    handleGoToPrevStep={handleBackFromEmailVerification}
                  />
                )}
                {step === STEP.REGISTRATION_CONFIRMATION && (
                  <RegistrationConfirmation
                    formFields={formFields}
                    queryParams={queryParams}
                    currentProvider={currentProvider}
                    handleGoToNextStep={handleGoToNextStep}
                  />
                )}
                {step === STEP.ADDITIONAL_USER_INFORMATION && (
                  <AdditionalUserInformation
                    handleGoToNextStep={handleGoToNextStep}
                    handleGoToPrevStep={handleGoToPrevStep}
                  />
                )}
                {step === STEP.REGISTRATION_PROCESS_COMPLETION && (
                  <RegistrationProcessCompletion
                    formFields={formFields}
                    queryParams={queryParams}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  if (tpaHint) {
    if (thirdPartyAuthApiStatus === PENDING_STATE) {
      return <Skeleton height={36} />;
    }
    const { provider, skipHintedLogin } = getTpaProvider(tpaHint, providers, secondaryProviders);
    if (skipHintedLogin) {
      window.location.href = getConfig().LMS_BASE_URL + provider.registerUrl;
      return null;
    }
    return provider ? <EnterpriseSSO provider={provider} /> : renderForm();
  }
  return (
    renderForm()
  );
};

RegistrationPage.propTypes = {
  institutionLogin: PropTypes.bool,
  // Actions
  handleInstitutionLogin: PropTypes.func,
  onSelectPage: PropTypes.func,
};

RegistrationPage.defaultProps = {
  handleInstitutionLogin: null,
  institutionLogin: false,
  onSelectPage: () => { },
};

export default RegistrationPage;
